import {
  BURST_PEAK_DB,
  dbToGain,
  type BurstSource,
  type EventSource,
  type FilterPlan,
  type NoiseSource,
} from './plan.js';

/**
 * The synthesised layers' buffers, made once when a world loads (design,
 * "Layers"): noise in a colour, shaped by filters and made to loop without a
 * seam, and sounds rendered as events -- waves breaking, water lapping,
 * bubbles (Minnaert chirps), a clock's tick, a mill's sails. Plain arithmetic
 * on samples, seeded, so a test hears exactly what a headset does; the live
 * graph only plays these back, slowly wobbled. No node is made while a lesson
 * plays.
 */

/** Noise loops this long: long enough that no pattern is heard in it. */
export const NOISE_SECONDS = 8;
/** Every synthesised buffer is made to this RMS: the plans' levels are against it. */
export const SYNTH_RMS_DB = -24;
/** Its loop's seam, crossfaded in equal power. */
const SEAM_SECONDS = 0.5;

export type Random = () => number;

/** mulberry32: small, fast, and the same everywhere. */
export function seeded(seed: number): Random {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A seed from a name, so each layer of each world has its own noise. */
export function seedOf(name: string): number {
  let h = 2166136261;
  for (let i = 0; i < name.length; i++) h = Math.imul(h ^ name.charCodeAt(i), 16777619);
  return h >>> 0;
}

/**
 * The rate a synthesised buffer is made at: half the context's for what has
 * nothing above 10 kHz (brown noise, anything low-passed, the events), which
 * halves its memory; the context resamples it as it plays.
 */
export function synthRate(source: NoiseSource | EventSource): 24_000 | 48_000 {
  if (source.kind === 'events') return 24_000;
  if (source.colour === 'brown') return 24_000;
  const lowpass = (source.shape ?? []).filter((f) => f.type !== 'highpass');
  return lowpass.some((f) => f.frequency <= 6000) ? 24_000 : 48_000;
}

/** How long a synthesised buffer is. */
export const synthSeconds = (source: NoiseSource | EventSource): number =>
  source.kind === 'noise' ? NOISE_SECONDS : source.seconds;

/** Bytes it takes decoded (float32, mono): for the budget. */
export const synthBytes = (source: NoiseSource | EventSource): number =>
  Math.round(synthSeconds(source) * synthRate(source)) * 4;

/** White, pink (Paul Kellet's filter) or brown (leaky integrated) noise. */
export function noise(colour: NoiseSource['colour'], length: number, random: Random): Float32Array {
  const out = new Float32Array(length);
  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  let b3 = 0;
  let b4 = 0;
  let b5 = 0;
  let b6 = 0;
  let brown = 0;
  for (let i = 0; i < length; i++) {
    const white = random() * 2 - 1;
    if (colour === 'white') {
      out[i] = white;
    } else if (colour === 'pink') {
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.969 * b2 + white * 0.153852;
      b3 = 0.8665 * b3 + white * 0.3104856;
      b4 = 0.55 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.016898;
      out[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
      b6 = white * 0.115926;
    } else {
      brown = (brown + 0.02 * white) / 1.02;
      out[i] = brown * 3.5;
    }
  }
  return out;
}

/** A second-order filter (the RBJ cookbook), in place. */
export function filter(samples: Float32Array, plan: FilterPlan, rate: number): Float32Array {
  const w = (2 * Math.PI * Math.min(plan.frequency, rate * 0.45)) / rate;
  const q = plan.q ?? Math.SQRT1_2;
  const alpha = Math.sin(w) / (2 * q);
  const cos = Math.cos(w);
  let b0: number;
  let b1: number;
  let b2: number;
  if (plan.type === 'lowpass') {
    b0 = (1 - cos) / 2;
    b1 = 1 - cos;
    b2 = (1 - cos) / 2;
  } else if (plan.type === 'highpass') {
    b0 = (1 + cos) / 2;
    b1 = -(1 + cos);
    b2 = (1 + cos) / 2;
  } else {
    b0 = alpha;
    b1 = 0;
    b2 = -alpha;
  }
  const a0 = 1 + alpha;
  const a1 = -2 * cos;
  const a2 = 1 - alpha;
  let x1 = 0;
  let x2 = 0;
  let y1 = 0;
  let y2 = 0;
  for (let i = 0; i < samples.length; i++) {
    const x = samples[i]!;
    const y = (b0 * x + b1 * x1 + b2 * x2 - a1 * y1 - a2 * y2) / a0;
    x2 = x1;
    x1 = x;
    y2 = y1;
    y1 = y;
    samples[i] = y;
  }
  return samples;
}

export function rmsDb(samples: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i]! * samples[i]!;
  return 10 * Math.log10(sum / Math.max(1, samples.length) + 1e-20);
}

/** To the plans' reference RMS, but never past full scale at its peak. */
export function normalise<T extends Float32Array>(samples: T, targetDb = SYNTH_RMS_DB): T {
  let gain = 10 ** ((targetDb - rmsDb(samples)) / 20);
  let peak = 0;
  for (let i = 0; i < samples.length; i++) peak = Math.max(peak, Math.abs(samples[i]!));
  if (peak * gain > 0.95) gain = 0.95 / peak;
  for (let i = 0; i < samples.length; i++) samples[i]! *= gain;
  return samples;
}

/** `length` samples from `longer`, its tail after them crossfaded into its head: a seamless loop. */
function loopOf(longer: Float32Array, length: number, fade: number): Float32Array<ArrayBuffer> {
  const out = longer.slice(0, length);
  for (let i = 0; i < fade; i++) {
    const t = (i + 0.5) / fade;
    out[i] =
      out[i]! * Math.sin((t * Math.PI) / 2) + longer[length + i]! * Math.cos((t * Math.PI) / 2);
  }
  return out;
}

/** A noise layer's buffer: coloured, shaped, looping without a seam, at the reference RMS. */
export function noiseBuffer(
  source: NoiseSource,
  rate: number,
  seed: number,
): Float32Array<ArrayBuffer> {
  const length = Math.round(NOISE_SECONDS * rate);
  const fade = Math.round(SEAM_SECONDS * rate);
  // A second's run-up, so the filters have settled before the part that is kept.
  const runUp = rate;
  const raw = noise(source.colour, runUp + length + fade, seeded(seed));
  for (const plan of source.shape ?? []) filter(raw, plan, rate);
  return normalise(loopOf(raw.subarray(runUp), length, fade));
}

/** Adds `sound` into `out` from `at`, wrapping round its end: an event across the seam loops too. */
function mixWrapped(out: Float32Array, sound: Float32Array, at: number): void {
  for (let i = 0; i < sound.length; i++) out[(at + i) % out.length]! += sound[i]!;
}

/** Noise through a band, under an envelope: a wave's break, a sail's swish. */
function burst(
  length: number,
  random: Random,
  band: readonly FilterPlan[],
  rate: number,
  envelope: (t: number) => number,
): Float32Array {
  const sound = noise('white', length, random);
  for (const plan of band) filter(sound, plan, rate);
  for (let i = 0; i < length; i++) sound[i]! *= envelope(i / length);
  return sound;
}

/** An event layer's buffer: its sounds rendered once, looping, at the reference RMS. */
export function eventBuffer(
  source: EventSource,
  rate: number,
  seed: number,
): Float32Array<ArrayBuffer> {
  const random = seeded(seed);
  const out = new Float32Array(Math.round(source.seconds * rate));
  const seconds = (s: number): number => Math.round(s * rate);
  const between = (a: number, b: number): number => a + random() * (b - a);

  if (source.event === 'waves' || source.event === 'lapping') {
    const lapping = source.event === 'lapping';
    // The wash between the breaks, never quite gone.
    const wash = noiseBuffer(
      {
        kind: 'noise',
        colour: 'pink',
        shape: [{ type: 'lowpass', frequency: lapping ? 700 : 1400 }],
      },
      rate,
      seed ^ 0x5eed,
    );
    for (let i = 0; i < out.length; i++) out[i] = wash[i % wash.length]! * (lapping ? 0.25 : 0.3);
    for (let at = 0; at < out.length;) {
      const rise = lapping ? between(0.12, 0.25) : between(0.6, 1.1);
      const fall = lapping ? between(0.4, 0.8) : between(2.5, 4.5);
      const length = seconds(rise + fall);
      const peak = between(0.7, 1);
      const sound = burst(
        length,
        random,
        [{ type: 'lowpass', frequency: lapping ? between(500, 900) : between(1200, 2400) }],
        rate,
        (t) => {
          const s = t * (rise + fall);
          return s < rise
            ? peak * Math.sin(((s / rise) * Math.PI) / 2) ** 2
            : peak * Math.exp(-(s - rise) / (fall / 3));
        },
      );
      mixWrapped(out, sound, at);
      at += seconds(lapping ? between(1.6, 3.2) : between(5, 9));
    }
  } else if (source.event === 'bubbles') {
    // Minnaert chirps: a bubble rings at a pitch set by its size, rising as it goes.
    for (let at = seconds(between(0, 0.4)); at < out.length;) {
      const train = random() < 0.25 ? Math.floor(between(3, 7)) : 1;
      for (let k = 0; k < train; k++) {
        const f0 = between(350, 1500);
        const length = seconds(between(0.03, 0.09));
        const sound = new Float32Array(length);
        let phase = 0;
        for (let i = 0; i < length; i++) {
          const t = i / rate;
          phase += (2 * Math.PI * f0 * (1 + 3 * t)) / rate;
          sound[i] = Math.sin(phase) * Math.exp(-t / (length / rate / 4)) * between(0.8, 1);
        }
        mixWrapped(out, sound, at + seconds(k * between(0.04, 0.12)));
      }
      at += seconds(between(0.25, 1.4));
    }
  } else if (source.event === 'tick') {
    // A wall clock's tick: a click and a short high ring, once a second.
    const length = seconds(0.03);
    const click = burst(length, random, [{ type: 'highpass', frequency: 1500 }], rate, (t) =>
      Math.exp(-t * 12),
    );
    for (let i = 0; i < length; i++) {
      click[i]! += 0.5 * Math.sin((2 * Math.PI * 2200 * i) / rate) * Math.exp(-i / seconds(0.006));
    }
    for (let at = 0; at < out.length; at += seconds(1)) mixWrapped(out, click, at);
  } else {
    // A mill's sails: `count` swishes a turn, evenly.
    const count = source.count ?? 4;
    const every = out.length / count;
    for (let k = 0; k < count; k++) {
      const length = seconds(0.9);
      const sound = burst(
        length,
        random,
        [
          { type: 'highpass', frequency: 250 },
          { type: 'lowpass', frequency: 1400 },
        ],
        rate,
        (t) => Math.sin(Math.PI * t) ** 2,
      );
      mixWrapped(out, sound, Math.round(k * every));
    }
  }
  return normalise(out);
}

/** A synthesised layer's buffer. */
export function synthBuffer(
  source: NoiseSource | EventSource,
  seed: number,
): Float32Array<ArrayBuffer> {
  const rate = synthRate(source);
  return source.kind === 'noise'
    ? noiseBuffer(source, rate, seed)
    : eventBuffer(source, rate, seed);
}

/** An eruption's takes (plan.ts, `BurstSource`): made at this rate, all low-passed. */
export const BURST_RATE = 24_000;
/** A boom: a 15–80 ms rise, then its tail dying away over the rest. */
export const BOOM_SECONDS = 1.6;
/** A lava bomb's pops: a few crackles, all within this. */
export const POP_SECONDS = 0.35;
const BOOM_TAKES = 4;
const POP_TAKES = 6;

/** Bytes an eruption's takes take decoded: for the budget. */
export const burstBytes = (): number =>
  Math.round((BOOM_TAKES * BOOM_SECONDS + POP_TAKES * POP_SECONDS) * BURST_RATE) * 4;

/** To `db` dBFS at its peak. */
function peakTo<T extends Float32Array>(samples: T, db: number): T {
  let top = 0;
  for (let i = 0; i < samples.length; i++) top = Math.max(top, Math.abs(samples[i]!));
  const gain = top > 0 ? dbToGain(db) / top : 0;
  for (let i = 0; i < samples.length; i++) samples[i]! *= gain;
  return samples;
}

/** The last `seconds` of a take faded to nothing, so its end never clicks. */
function fadeEnd(samples: Float32Array, seconds: number, rate: number): void {
  const n = Math.min(samples.length, Math.round(seconds * rate));
  for (let i = 0; i < n; i++) samples[samples.length - 1 - i]! *= i / n;
}

/**
 * A deep distant boom: pink noise between 60 and 320 Hz (the Quest's small
 * speakers carry 150–400 Hz; the distance takes the rest), rising over
 * 25–45 ms -- fast, but no startle -- and dying away over its 1.6 s.
 */
function boomTake(random: Random): Float32Array<ArrayBuffer> {
  const rate = BURST_RATE;
  const length = Math.round(BOOM_SECONDS * rate);
  const runUp = Math.round(0.5 * rate);
  const raw = noise('pink', runUp + length, random);
  for (const plan of [
    { type: 'highpass', frequency: 60 },
    { type: 'lowpass', frequency: 320 },
    { type: 'lowpass', frequency: 320 },
  ] as const)
    filter(raw, plan, rate);
  const sound = raw.slice(runUp);
  const attack = 0.025 + random() * 0.02;
  // The blast, then the rolling echo it leaves.
  const blast = 0.09 + random() * 0.05;
  const roll = 0.35 + random() * 0.15;
  for (let i = 0; i < length; i++) {
    const t = i / rate;
    const after = t - attack;
    sound[i]! *=
      t < attack
        ? Math.sin(((t / attack) * Math.PI) / 2) ** 2
        : 0.7 * Math.exp(-after / blast) + 0.3 * Math.exp(-after / roll);
  }
  fadeEnd(sound, 0.05, rate);
  return peakTo(sound, BURST_PEAK_DB);
}

/** A lava bomb landing: two to five crackles in its first quarter second, muffled. */
function popTake(random: Random, db: number): Float32Array<ArrayBuffer> {
  const rate = BURST_RATE;
  const sound = new Float32Array(Math.round(POP_SECONDS * rate));
  const count = 2 + Math.floor(random() * 4);
  for (let k = 0; k < count; k++) {
    const at = Math.round((0.005 + random() * 0.24) * rate);
    const attack = 0.0015 + random() * 0.0015;
    const decay = 0.003 + random() * 0.005;
    const size = 0.4 + random() * 0.6;
    const n = Math.round((attack + decay * 6) * rate);
    for (let i = 0; i < n && at + i < sound.length; i++) {
      const t = i / rate;
      const envelope =
        t < attack ? Math.sin(((t / attack) * Math.PI) / 2) ** 2 : Math.exp(-(t - attack) / decay);
      sound[at + i]! += (random() * 2 - 1) * envelope * size;
    }
  }
  for (const plan of [
    { type: 'highpass', frequency: 300 },
    { type: 'lowpass', frequency: 1800 },
    { type: 'lowpass', frequency: 1800 },
  ] as const)
    filter(sound, plan, rate);
  fadeEnd(sound, 0.02, rate);
  return peakTo(sound, BURST_PEAK_DB + db);
}

/** An eruption's takes, rendered once when the world loads: seeded, as every synthesised sound. */
export function burstTakes(
  source: BurstSource,
  seed: number,
): { rate: number; booms: Float32Array<ArrayBuffer>[]; pops: Float32Array<ArrayBuffer>[] } {
  const random = seeded(seed);
  return {
    rate: BURST_RATE,
    booms: Array.from({ length: BOOM_TAKES }, () => boomTake(random)),
    pops: Array.from({ length: POP_TAKES }, () => popTake(random, source.popDb)),
  };
}
